/**
 * Navigation Guard Context
 *
 * Provides a way for forms to register unsaved changes and prevent navigation.
 * Used by MainLayout to show confirmation modals when navigating away from dirty forms.
 */

import { createContext, useContext, useState, ReactNode } from 'react';

interface NavigationGuardContextValue {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
}

const NavigationGuardContext = createContext<NavigationGuardContextValue | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useNavigationGuard = () => {
  const context = useContext(NavigationGuardContext);
  if (!context) {
    // Return a no-op implementation if used outside provider
    return {
      hasUnsavedChanges: false,
      setHasUnsavedChanges: () => {},
    };
  }
  return context;
};

interface NavigationGuardProviderProps {
  children: ReactNode;
}

export const NavigationGuardProvider = ({ children }: NavigationGuardProviderProps) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  return (
    <NavigationGuardContext.Provider
      value={{
        hasUnsavedChanges,
        setHasUnsavedChanges,
      }}
    >
      {children}
    </NavigationGuardContext.Provider>
  );
};
